/**
 * Is a source still reporting? "Silence is not calm."
 *
 * A station that stops sending does not mean the wind stopped. Every panel shows how old its
 * reading is, and a source that falls silent moves to "late" and then "not reporting", which
 * the limits engine treats as no evidence (it never clears an active alert). This file is
 * the **one** definition of those thresholds: the limits engine's source health
 * (`limits/health.ts`) calls `stalenessThresholds` rather than keeping its own arithmetic, so
 * a panel's "late" badge and the engine's "not reporting" alert can never disagree.
 *
 * Thresholds scale with the source's own `SourceDescriptor.expectedIntervalS`:
 *
 * - **late** once the reading is older than `LATE_FACTOR` (1.5) × the interval. One report
 *   has been missed by half an interval: worth showing, not yet worth an alarm.
 * - **not reporting** once older than `NOT_REPORTING_FACTOR` (3) × the interval: two whole
 *   reports missed. A single dropped packet or a slow upload never trips it.
 * - Both get a **floor** (`MIN_LATE_S` 90 s, `MIN_NOT_REPORTING_S` 180 s). A Tempest hub
 *   speaks every 3 s; 1.5 × 3 s would flag "late" on any Wi-Fi hiccup, and the on-site bridge
 *   only sends one summary a minute anyway. The floors are exactly what the factors give a
 *   once-a-minute source, so they change nothing for a source reporting once a minute or
 *   less often, and only stop a faster one being twitchier than the bridge itself.
 * - An optional **staleness grace** (`graceS`, default 0) is added to both, for a source whose
 *   reports are published some known time after they are observed.
 *
 * **Airport reports.** Routine METARs are issued once an hour (usually observed around
 * :50–:55), with SPECIs in between when conditions change, and reach NOAA's feed a few
 * minutes after observation. An airport source therefore uses `AIRPORT_REPORT_INTERVAL_S`
 * (3600 s), and the 1.5× factor *is* the allowance: late at 90 min, i.e. the next routine
 * report is 30 minutes overdue, far past any normal publication delay; not reporting at 3 h,
 * two routine reports missing, which in practice means the station or the feed is down. An
 * hourly source flagged "late" at 61 minutes would be wrong every hour, which is why the
 * interval must be the routine cadence and not "how often we poll".
 */

import type { SourceDescriptor, UtcMs } from './types.js'

export type ReportingState = 'reporting' | 'late' | 'not_reporting'

/** Late past this multiple of the expected interval. */
export const LATE_FACTOR = 1.5
/** Not reporting past this multiple of the expected interval. */
export const NOT_REPORTING_FACTOR = 3
/** Never "late" sooner than this, however frequent the source (1.5 bridge minutes). */
export const MIN_LATE_S = 90
/** Never "not reporting" sooner than this (3 bridge minutes). */
export const MIN_NOT_REPORTING_S = 180

/** Routine airport reports are hourly; use this as an airport source's `expectedIntervalS`. */
export const AIRPORT_REPORT_INTERVAL_S = 3600

export interface StalenessOptions {
  lateFactor?: number
  notReportingFactor?: number
  minLateS?: number
  minNotReportingS?: number
  /** Added to both thresholds; for sources published some time after observation. */
  graceS?: number
}

export interface StalenessThresholds {
  /** Age (s) above which the source is late. */
  lateAfterS: number
  /** Age (s) above which the source is not reporting. */
  notReportingAfterS: number
}

/** The two thresholds for a source with this expected interval. */
export function stalenessThresholds(expectedIntervalS: number, options: StalenessOptions = {}): StalenessThresholds {
  const interval = Number.isFinite(expectedIntervalS) && expectedIntervalS > 0 ? expectedIntervalS : 0
  const grace = options.graceS ?? 0
  const lateAfterS = Math.max(options.minLateS ?? MIN_LATE_S, interval * (options.lateFactor ?? LATE_FACTOR) + grace)
  const notReportingAfterS = Math.max(
    options.minNotReportingS ?? MIN_NOT_REPORTING_S,
    interval * (options.notReportingFactor ?? NOT_REPORTING_FACTOR) + grace,
    lateAfterS,
  )
  return { lateAfterS, notReportingAfterS }
}

export interface Staleness {
  state: ReportingState
  /**
   * Seconds since the reading was observed, or `null` when the source has never reported.
   * A reading stamped in the future (a station clock running fast) is reported as age 0,
   * because a negative age would read as "fresher than now".
   */
  ageS: number | null
  thresholds: StalenessThresholds
}

/**
 * The reporting state of a source given its last observation time.
 *
 * Boundaries: exactly at `lateAfterS` is still `reporting`; one millisecond past it is
 * `late`. "Older than 1.5× the interval" is the rule, so equality is not older.
 * `lastObservedAt === null` (never reported) is `not_reporting`.
 */
export function staleness(
  expectedIntervalS: number,
  lastObservedAt: UtcMs | null,
  now: UtcMs,
  options: StalenessOptions = {},
): Staleness {
  const thresholds = stalenessThresholds(expectedIntervalS, options)
  if (lastObservedAt === null || !Number.isFinite(lastObservedAt)) {
    return { state: 'not_reporting', ageS: null, thresholds }
  }
  const ageS = Math.max(0, (now - lastObservedAt) / 1000)
  let state: ReportingState = 'reporting'
  if (ageS > thresholds.notReportingAfterS) state = 'not_reporting'
  else if (ageS > thresholds.lateAfterS) state = 'late'
  return { state, ageS, thresholds }
}

/** Convenience: staleness for a `SourceDescriptor`, using its `expectedIntervalS`. */
export function sourceStaleness(
  source: Pick<SourceDescriptor, 'expectedIntervalS'>,
  lastObservedAt: UtcMs | null,
  now: UtcMs,
  options: StalenessOptions = {},
): Staleness {
  return staleness(source.expectedIntervalS, lastObservedAt, now, options)
}

/**
 * "observed 14 min ago" phrasing for an age in seconds. No locale or timezone work here:
 * the screen adds the clock time itself.
 *
 * Minutes are floored, the way a clock is read ("14 min ago" means 14–15 minutes), and the
 * state colour carries the warning, not the wording. Under a minute is "just now"; from an
 * hour, "1 h 5 min ago"; from a day, "2 days ago".
 */
export function describeAge(ageS: number | null): string {
  if (ageS === null || !Number.isFinite(ageS)) return 'never reported'
  const s = Math.max(0, ageS)
  if (s < 60) return 'just now'
  const totalMin = Math.floor(s / 60)
  if (totalMin < 60) return `${totalMin} min ago`
  const totalH = Math.floor(totalMin / 60)
  if (totalH < 24) {
    const m = totalMin - totalH * 60
    return m === 0 ? `${totalH} h ago` : `${totalH} h ${m} min ago`
  }
  const d = Math.floor(totalH / 24)
  return d === 1 ? '1 day ago' : `${d} days ago`
}

/** "observed 14 min ago". */
export function observedAgo(ageS: number | null): string {
  if (ageS === null || !Number.isFinite(ageS)) return 'never reported'
  return `observed ${describeAge(ageS)}`
}
